import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    DataGridPayLoad,
    DataGridState,
    DataGridStore,
} from 'common/types/datagrid';
import { GridSortDirection } from '@mui/x-data-grid-premium';
import { defaultResultsPerPage } from 'common/components/data-grid/DataGridUtils';
import { isEmpty } from 'common/utils/Utils';

export const CLEAR_ALL_ADVANCED_FILTER_ID_INITIAL = 'INIT';
const defaultState: DataGridState = {
    data: [],
    totalCount: 0,
    activePaginationPage: 1,
    dataLoading: true,
    initialized: false,
    activeFilters: [],
    searchFilters: {},
    advancedFilters: {},
    advancedFilterCount: 0,
    activeQuery: {
        limit: defaultResultsPerPage,
        skip: 0,
    },
    columnBufferPx: 150,
    columnVisibility: {},
    columnDimensions: {},
    pinnedColumns: {
        left: [],
        right: [],
    },
    density: 'compact' as const,
    sortModel: [],
    rowGroupingModel: [],
    columnOrdering: [],
    activeId: 0,
    filterModel: { items: [] },
    disableQueryRules: false,
    clearAllAdvancedFilterId: CLEAR_ALL_ADVANCED_FILTER_ID_INITIAL,
};

const lineItemDefaultState = {
    ...defaultState,
    sortModel: [{ field: 'lineNumber', sort: 'asc' as GridSortDirection }],
    columnVisibility: {
        id: false,
    },
    pinnedColumns: {
        left: ['lineNumber'],
        right: ['actions'],
    },
};

const initialState: DataGridStore = {
    ADMIN_ASSET: defaultState,
    ADMIN_CONTRACTOR: defaultState,
    ADMIN_ORDER: defaultState,
    ADMIN_ORDER_DRAWER: defaultState,
    ADMIN_ORDER_RULE: defaultState,
    ADMIN_WORK_ORDER: defaultState,
    ADMIN_PROJECT: defaultState,
    ADMIN_PROJECT_LIST: defaultState,
    ADMIN_PROJECT_WORK_ORDER_LIST: {
        ...defaultState,
        columnOrdering: [
            'select',
            'propertyAddress',
            'numberOfSystem',
            'workOrderTypeDisplay',
            'issueId',
            'issueReasonDisplay',
            'issueSecondaryReasonDisplay',
            'number',
            'created',
            'scheduleStartDateTime',
            'statusDisplay',
            'poNumber',
            'reference1',
            'reference2',
            'contractor',
        ],
    },
    ADMIN_TEMPLATE_QUOTE: defaultState,
    ADMIN_TEMPLATE_CLIENT: defaultState,
    ADMIN_TEMPLATE_CHECKLIST: defaultState,
    ADMIN_QUOTE: defaultState,
    ADMIN_QUOTE_EQUIPMENT_ONLY: defaultState,
    ADMIN_FINANCE_SYNC_TASK: defaultState,
    ADMIN_FINANCE_SALES_INVOICE: defaultState,
    ADMIN_FINANCE_PURCHASE_INVOICE: defaultState,
    ADMIN_FINANCE_POSTAL_CODE_LABOR_RATE: defaultState,
    ADMIN_FINANCE_CONTRACTOR_LABOR_RATE: defaultState,
    ADMIN_FINANCE_CLIENT_LABOR_RATE: defaultState,
    ADMIN_FINANCE_PROJECT_LABOR_RATE: defaultState,
    ADMIN_CONTRACTOR_FILE: defaultState,
    ADMIN_CONTRACTOR_REGISTRATION: defaultState,
    ADMIN_TSM_MANAGEMENT: defaultState,
    ADMIN_INVOICE: defaultState,
    ADMIN_ORDER_CLONE: defaultState,
    AHRI: defaultState,
    DISTRIBUTION_INVENTORY: defaultState,
    PRODUCT_SELECTOR: defaultState,
    PRODUCT_SEARCH_LIST: defaultState,
    SAVED_ORDER: defaultState,
    SHARED_SAVED_ORDER: defaultState,
    WARRANTY_CONTACT: defaultState,
    WARRANTY_OWNER: defaultState,
    DISTRIBUTION_CENTER_AND_INVENTORY_SEARCH: defaultState,
    DISTRIBUTION_CENTER_AND_INVENTORY_SEARCH_CONTRACTOR: defaultState,
    DISTRIBUTION_CENTER_AND_INVENTORY_SEARCH_CLIENT: defaultState,
    TAX_TRANSACTION_HISTORY: defaultState,
    INVOICE_DOWNLOAD_HISTORIES: defaultState,
    CLIENT_ORDER: defaultState,
    CLIENT_PROPERTY: defaultState,
    CLIENT_INVOICE: defaultState,
    CLIENT_SHARED_INVOICE: defaultState,
    CLIENT_TEAMS: defaultState,
    CLIENT_MANAGE_CHILD_CLIENTS: defaultState,
    CLIENT_TEAM_PERMISSIONS: defaultState,
    CONTRACTOR_ORDERS: defaultState,
    CONTRACTOR_INVOICE: defaultState,
    CONTRACTOR_SHARED_INVOICE: defaultState,
    CONTRACTOR_QUOTES: defaultState,
    CONTRACTOR_WORK_ORDERS: defaultState,
    CONTRACTOR_AVAILABLE_JOBS: defaultState,
    CONTRACTOR_TEAMS: defaultState,
    CONTRACTOR_TEAM_FILES: defaultState,
    CONTRACTOR_FILES: defaultState,
    DOCUMENT_FILES: defaultState,
    LINE_ITEM_ORDER: lineItemDefaultState,
    LINE_ITEM_WORK_ORDER: lineItemDefaultState,
    LINE_ITEM_QUOTE: lineItemDefaultState,
    LINE_ITEM_ORDER_LABOR_WARRANTY_CREATION: lineItemDefaultState,
    LINE_ITEM_ORDER_LABOR_WARRANTY: lineItemDefaultState,
    LINE_ITEM_ORDER_LABOR_WARRANTY_FULFILL_MODAL: lineItemDefaultState,
    LINE_ITEM_ORDER_FULFILL_MODAL: lineItemDefaultState,
    LINE_ITEM_RE_ORDER_MODAL: lineItemDefaultState,
    LINE_ITEM_WORK_ORDER_COMPLETE_MODAL: lineItemDefaultState,
    LINE_ITEM_ORDER_CONFIRMATION_MODAL_WITH_PRICE: lineItemDefaultState,
    LINE_ITEM_ORDER_CONFIRMATION_MODAL_WITHOUT_PRICE: lineItemDefaultState,
    LINE_ITEM_QUOTE_APPROVE_MODAL: lineItemDefaultState,
    LINE_ITEM_QUOTE_CONFIRM_ORDER_MODAL: lineItemDefaultState,
    LINE_ITEM_INSPECTION_WORK_ORDER_SUBMIT_MODAL: lineItemDefaultState,
    LINE_ITEM_WORK_ORDER_SUBMIT_MODAL_WITH_STATUS: lineItemDefaultState,
    LINE_ITEM_WORK_ORDER_SUBMIT_MODAL_WITHOUT_STATUS: lineItemDefaultState,
    LINE_ITEM_ORDER_QUOTE_GENERATE_PDF_MODAL: lineItemDefaultState,
    LINE_ITEM_QUOTE_GENERATE_PDF_PRICE: lineItemDefaultState,
    LINE_ITEM_QUOTE_GENERATE_PDF_PAYOUT: lineItemDefaultState,
    LINE_ITEM_ORDER_GENERATE_PDF_MODAL: lineItemDefaultState,
    LINE_ITEM_ORDER_CONTRACTOR_INVOICED: lineItemDefaultState,
    LINE_ITEM_DRAFT: lineItemDefaultState,
    LINE_ITEM_BULK_QUOTE: lineItemDefaultState,
    LINE_ITEM_BULK_WORK_ORDER: lineItemDefaultState,
    LINE_ITEM_QUOTE_TEMPLATE: lineItemDefaultState,
    LINE_ITEM_PURCHASE_INVOICE: lineItemDefaultState,
    LINE_ITEM_SALES_INVOICE: lineItemDefaultState,
    LINE_ITEM_PURCHASE_INVOICE_ADJUSTMENT: lineItemDefaultState,
    LINE_ITEM_SALES_INVOICE_ADJUSTMENT: lineItemDefaultState,
    LINE_ITEM_RETURN: lineItemDefaultState,
    LINE_ITEM_PAST_RETURN: lineItemDefaultState,
    LINE_ITEM_WORK_ORDER_GENERATE_PDF: lineItemDefaultState,
    LINE_ITEM_CREATE_RETURN: lineItemDefaultState,
    LINE_ITEM_PROPERTY_HISTORY: lineItemDefaultState,
    LINE_ITEM_PROPERTY_HISTORY_WITH_STATUS: lineItemDefaultState,
    LINE_ITEM_FINANCE: lineItemDefaultState,
    LINE_ITEM_FINANCE_RETURN: lineItemDefaultState,
    LINE_ITEM_FINANCE_CREATE_CREDIT: lineItemDefaultState,
    SERIAL_NUMBER_SEARCH: defaultState,
};

export const dataGridStateSlice = createSlice({
    name: 'dataGridState',
    initialState,
    reducers: {
        resetGridState: () => {
            return initialState;
        },
        resetCurrentGridState: (
            state: DataGridStore,
            action: PayloadAction<Pick<DataGridPayLoad, 'gridName'>>
        ) => {
            const { gridName } = action.payload;
            state[gridName] = { ...initialState[gridName] };
        },
        initializeGridState: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'initialState'>
            >
        ) => {
            const { gridName, initialState: _initialState } = action.payload;
            if (!state[gridName]?.initialized) {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    ...(_initialState && _initialState),
                    initialized: true,
                };
            }
        },
        deleteGridState: (
            state: DataGridStore,
            action: PayloadAction<DataGridPayLoad>
        ) => {
            const { gridName } = action.payload;

            state[gridName] = { ...initialState[gridName] };
        },
        loadGridState: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'initialState'>
            >
        ) => {
            const { gridName, initialState: selectedState } = action.payload;
            const storedColumnOrdering =
                initialState[gridName]?.columnOrdering ?? [];
            const newColumnOrdering = selectedState?.columnOrdering ?? [];

            // Identify newly defined columns before creating the saved view, so they can be hidden or appended for column ordering.
            const newColumns = [storedColumnOrdering, newColumnOrdering].reduce(
                (a, b) => a.filter(c => !b.includes(c)),
                []
            );

            state[gridName] = {
                ...(initialState[gridName] && initialState[gridName]),
                ...(selectedState && selectedState),
                columnVisibility: {
                    ...selectedState?.columnVisibility,
                    ...Object.fromEntries(newColumns.map(c => [c, false])),
                },
                columnOrdering: [
                    ...(selectedState?.columnOrdering ?? []),
                    ...newColumns,
                ],
                activeQuery: {
                    ...selectedState?.activeQuery,
                    limit:
                        selectedState?.activeQuery?.limit ||
                        defaultResultsPerPage,
                },
            };
        },
        setColumnVisibility: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'columnVisibility'>
            >
        ) => {
            const { gridName, columnVisibility } = action.payload;
            if (state[gridName]) {
                state[gridName].columnVisibility = columnVisibility;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    columnVisibility,
                };
            }
        },
        setPinnedColumns: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'pinnedColumns'>
            >
        ) => {
            const { gridName, pinnedColumns } = action.payload;
            if (state[gridName]) {
                state[gridName].pinnedColumns = pinnedColumns;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    pinnedColumns,
                };
            }
        },
        setSortModel: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'sortModel'>
            >
        ) => {
            const { gridName, sortModel } = action.payload;
            if (state[gridName]) {
                state[gridName].sortModel = sortModel;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    sortModel,
                };
            }
        },
        setDensity: (
            state: DataGridStore,
            action: PayloadAction<Pick<DataGridPayLoad, 'gridName' | 'density'>>
        ) => {
            const { gridName, density } = action.payload;
            if (state[gridName]) {
                state[gridName].density = density;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    density,
                };
            }
        },
        setColumnOrdering: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'columnOrdering'>
            >
        ) => {
            const { gridName, columnOrdering } = action.payload;
            if (state[gridName]) {
                state[gridName].columnOrdering = columnOrdering;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    columnOrdering,
                };
            }
        },
        setColumnDimensions: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'columnDimensions'>
            >
        ) => {
            const { gridName, columnDimensions } = action.payload;
            if (state[gridName]) {
                state[gridName].columnDimensions = columnDimensions;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    columnDimensions,
                };
            }
        },
        setActiveFilters: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'activeFilters'>
            >
        ) => {
            const { gridName, activeFilters } = action.payload;
            if (state[gridName]) {
                state[gridName].activeFilters = [...activeFilters];
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    activeFilters,
                };
            }
        },
        setSearchFilters: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'searchFilters'>
            >
        ) => {
            const { gridName, searchFilters } = action.payload;
            if (state[gridName]) {
                state[gridName].searchFilters = { ...searchFilters };
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    searchFilters,
                };
            }
        },
        setAdvancedFilters: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'advancedFilters'>
            >
        ) => {
            const { gridName, advancedFilters } = action.payload;
            if (state[gridName]) {
                state[gridName].advancedFilters = { ...advancedFilters };
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    advancedFilters,
                };
            }
        },
        resetClientAdvancedFilter: (
            state: DataGridStore,
            action: PayloadAction<Pick<DataGridPayLoad, 'gridName'>>
        ) => {
            const { gridName } = action.payload;
            const gridState = state[gridName];
            if (!gridState || !('client' in gridState.advancedFilters)) {
                return;
            }
            const removedClient = gridState.advancedFilters.client;
            const removedCount = Array.isArray(removedClient)
                ? removedClient.length
                : 1;
            delete gridState.advancedFilters.client;
            gridState.advancedFilterCount = Math.max(
                0,
                (gridState.advancedFilterCount ?? 0) - removedCount
            );
        },
        setRowGroupingModel: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'rowGroupingModel'>
            >
        ) => {
            const { gridName, rowGroupingModel } = action.payload;
            if (state[gridName]) {
                state[gridName].rowGroupingModel = rowGroupingModel;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    rowGroupingModel,
                };
            }
        },
        setGridViewActiveId: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'activeId'>
            >
        ) => {
            const { gridName, activeId } = action.payload;
            if (state[gridName]) {
                state[gridName].activeId = activeId;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    activeId,
                };
            }
        },
        setClearAllAdvancedFilterId: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'clearAllAdvancedFilterId'>
            >
        ) => {
            const { gridName, clearAllAdvancedFilterId } = action.payload;
            if (state[gridName]) {
                state[gridName].clearAllAdvancedFilterId =
                    clearAllAdvancedFilterId;
            }
        },
        setData: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<
                    DataGridPayLoad,
                    'gridName' | 'data' | 'reset' | 'concatenateResults'
                >
            >
        ) => {
            const {
                gridName,
                data = [],
                reset = false,
                concatenateResults = false,
            } = action.payload;
            if (state[gridName]) {
                // we only concatenate results for infinite scroll grids.
                // Has to be passed in explicitly.
                const newData =
                    concatenateResults && state[gridName].data && !reset
                        ? [...state[gridName].data, ...data]
                        : [...data];
                state[gridName].data = newData;
                state[gridName].dataLoading = false;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    data: [...data],
                    dataLoading: false,
                };
            }
        },
        setDataLoading: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'dataLoading'>
            >
        ) => {
            const { gridName, dataLoading } = action.payload;
            if (state[gridName]) {
                state[gridName].dataLoading = dataLoading;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    dataLoading,
                };
            }
        },
        setTotalCount: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'totalCount'>
            >
        ) => {
            const { gridName, totalCount } = action.payload;
            if (state[gridName]) {
                state[gridName].totalCount = totalCount;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    totalCount,
                };
            }
        },
        setActiveQuery: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'activeQuery'>
            >
        ) => {
            const { gridName, activeQuery } = action.payload;
            if (state[gridName]) {
                state[gridName].activeQuery = {
                    ...(!isEmpty(state[gridName].activeQuery) &&
                        state[gridName].activeQuery),
                    ...activeQuery,
                };
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    activeQuery: { ...activeQuery },
                };
            }
        },
        setActivePaginationPage: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'activePaginationPage'>
            >
        ) => {
            const { gridName, activePaginationPage } = action.payload;
            if (state[gridName]) {
                state[gridName].activePaginationPage = activePaginationPage;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    activePaginationPage,
                };
            }
        },
        setAdvancedFilterCount: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'advancedFilterCount'>
            >
        ) => {
            const { gridName, advancedFilterCount } = action.payload;
            if (state[gridName]) {
                state[gridName].advancedFilterCount = advancedFilterCount;
                state[gridName].dataLoading = false;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    advancedFilterCount,
                    dataLoading: false,
                };
            }
        },
        setDisableQueryRules: (
            state: DataGridStore,
            action: PayloadAction<
                Pick<DataGridPayLoad, 'gridName' | 'disableQueryRules'>
            >
        ) => {
            const { gridName, disableQueryRules } = action.payload;
            if (state[gridName]) {
                state[gridName].disableQueryRules = disableQueryRules;
            } else {
                state[gridName] = {
                    ...(initialState[gridName] && initialState[gridName]),
                    disableQueryRules,
                };
            }
        },
    },
});

export const {
    resetGridState,
    resetCurrentGridState,
    initializeGridState,
    loadGridState,
    deleteGridState,
    setColumnVisibility,
    setPinnedColumns,
    setSortModel,
    setColumnDimensions,
    setColumnOrdering,
    setDensity,
    setActiveFilters,
    setSearchFilters,
    setAdvancedFilters,
    resetClientAdvancedFilter,
    setRowGroupingModel,
    setGridViewActiveId,
    setTotalCount,
    setData,
    setDataLoading,
    setActiveQuery,
    setActivePaginationPage,
    setAdvancedFilterCount,
    setDisableQueryRules,
    setClearAllAdvancedFilterId,
} = dataGridStateSlice.actions;

export default dataGridStateSlice.reducer;
